import { useState, useRef } from "react";
import { Icon } from "@/components/Icon";
import Link from "next/link";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetClose,
  SheetTrigger,
} from "../Sidebar/Sidebar";
import IconsEnum from "enums/icons";
import * as gtag from "@/lib/gtag";

interface NavItem {
  title: string;
  link?: string;
  target?: string;
  active?: number;
  current?: number;
  children?: NavItem[];
  hasSubpages?: number;
  icon?: string;
  spacer?: number;
  navigationCols?: number;
}

interface NavClusterProps {
  title: string;
  icon?: string;
  items: NavItem[];
  navigationCols?: number;
  onItemClick?: (item: NavItem) => void;
  onClose?: () => void;
  onLinkClick?: (item: NavItem, clusterHeadline?: string) => void;
}

const NavigationSidebar = ({ content }: { content: NavItem[] }) => {
  const [activeLevel, setActiveLevel] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<NavItem | null>(
    null
  );
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const closeSidebar = () => {
    setActiveLevel(1);
    closeButtonRef.current?.click();
  };

  const handleBack = () => {
    setActiveLevel(1);
    setSelectedCategory(null);
  };

  const handleCategorySelect = (item: NavItem) => {
    if (item.children && item.children.length > 0) {
      setSelectedCategory(item);
      setActiveLevel(2);
    } else {
      handleLinkClick(item);
    }
  };

  const handleLinkClick = (item: NavItem, clusterHeadline?: string) => {
    handleTracking("itemClick", {
      firstLevel: clusterHeadline || item.title,
      secondLevel: clusterHeadline ? item.title : undefined,
    });
    // Close sidebar first
    closeSidebar();
  };

  const handleTracking = (caseType: "open" | "itemClick", data?: any) => {
    switch (caseType) {
      case "open":
        gtag.event({
          event: "menuOpen",
          menu: "burgerNavigation",
          action: "open",
          "gtm.uniqueEventId": 102,
        });
        break;
      case "itemClick":
        console.log("tracky", {
          event: "itemClick",
          itemClick: "burgerNavigation",
          firstLevel: data?.firstLevel || "Beliebteste Modelle",
          secondLevel: data?.secondLevel,
          position: "burgerMenu",
        });
        gtag.event({
          event: "itemClick",
          itemClick: "burgerNavigation",
          firstLevel: data?.firstLevel || "Beliebteste Modelle",
          secondLevel: data?.secondLevel,
          position: "burgerMenu",
        });
        break;
      default:
        console.warn(`Unknown tracking case type: ${caseType}`);
        break;
    }
  };

  if (!content || content.length === 0) return null;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          aria-label="Open navigation menu"
          onClick={() => handleTracking("open")}
        >
          <Icon type="menu" className="w-8 h-8 text-blue-light" />
        </button>
      </SheetTrigger>
      <SheetClose ref={closeButtonRef} className="hidden " />
      <SheetContent className="!pt-8  lg:!p-10 p-6 lg:min-w-[38vw] hd:w-1/4 lg:w-1/2 md:w-1/2 w-full !h-full !overflow-y-auto !overflow-x-hidden [&>button]:!fixed [&>button]:!top-6 [&>button]:!right-6 [&>button]:lg:!top-10  [&>button]:lg:!right-10 [&>button>svg]:h-6 [&>button>svg]:w-6 [&>button>svg]:text-blue-light [&>button]:!z-[999] [&>button]:bg-white [&>button]:!p-2 [&>button]:opacity-100 [&>button]:focus:ring-0 [&>button]:focus-within:ring-0">
        <SheetHeader>
          <div className="relative h-full border-t border-gray-300 mt-8  ">
            <div
              className={`${activeLevel === 2 ? "invisible" : "visible"} `}
              role="navigation"
              aria-label="Main navigation"
            >
              {content.map((category, index) => (
                <NavCluster
                  navigationCols={category?.navigationCols || 1}
                  key={index}
                  title={category?.title || "Title"}
                  icon={category?.icon || undefined}
                  items={category?.children || []}
                  onItemClick={handleCategorySelect}
                  onClose={closeSidebar}
                  onLinkClick={handleLinkClick}
                />
              ))}
            </div>

            {/* Level 2 Navigation */}
            <div
              className={`absolute inset-0 h-full overflow-y-auto bg-white transition-all duration-300 ${
                activeLevel === 2
                  ? "translate-x-0 opacity-100"
                  : "translate-x-full opacity-0"
              }`}
              role="navigation"
              aria-label="Submenu navigation"
            >
              <button
                onClick={handleBack}
                className="flex items-center gap-2 w-full text-2xl font-bold text-blue-dark border-b-[1px] border-gray-300 hover:text-blue-light mb-8 py-7"
                aria-label="Back to main menu"
              >
                <Icon type="arrow" className="w-7 h-7 rotate-180" />
                Hauptnavigation
              </button>
              <div className="space-y-4">
                <h3 className="text-3xl font-bold select-none mb-7 text-left">
                  {selectedCategory?.title}
                </h3>
                <div className="grid grid-cols-1 gap-y-4 ">
                  {selectedCategory?.children?.map((item, index) => (
                    <Link
                      key={index}
                      href={item.link || "#"}
                      target={item.target || "_self"}
                      className="flex cursor-pointer hover:text-blue-light focus:text-blue-light rounded px-2 py-1"
                      onClick={() =>
                        handleLinkClick(item, selectedCategory?.title)
                      }
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
};

const NavCluster: React.FC<NavClusterProps> = ({
  title,
  icon,
  items,
  onItemClick,
  navigationCols = 1,
  onClose,
  onLinkClick,
}) => {
  // safe check cuz we have some items with empty icon and the IconsEnum does not like that without default specified
  const showIcon = icon && icon !== "" && icon !== "0";
  const iconType = showIcon
    ? (IconsEnum[icon as keyof typeof IconsEnum] as unknown as string)
    : undefined;

  return (
    <div className="space-y-4 border-b-[1px] border-gray-300 py-7 text-sm">
      <h3 className="text-3xl font-bold flex items-center text-left gap-4 mb-6 select-none">
        {iconType && (
          <Icon type={iconType} className="w-7 h-7 text-orange-medium" />
        )}
        {title}
      </h3>
      <div
        className={`grid grid-cols-${navigationCols || 1} gap-y-5`}
        role="menu"
      >
        {items.map((item, index) => {
          const showItemIcon =
            item.icon && item.icon !== "" && item.icon !== "0";
          const itemIconType = showItemIcon
            ? (IconsEnum[
                item.icon as keyof typeof IconsEnum
              ] as unknown as string)
            : undefined;

          return !item.children || item.children.length === 0 ? (
            item.title === "Newsletter" || item.title === "Werkstatt" ? (
              <a
                key={index}
                href={item.link || "#"}
                target={item.target || "_self"}
                className="flex items-baseline text-left gap-4 w-2/3 justify-between group hover:text-blue-light focus:text-blue-light rounded px-2 py-1"
                role="menuitem"
                onClick={(e) => {
                  onClose?.();
                  onLinkClick?.(item, title);
                }}
              >
                <span className="flex items-center gap-4">
                  {itemIconType && (
                    <Icon type={itemIconType} className="w-7 h-7" />
                  )}
                  {item.title}
                </span>
              </a>
            ) : (
              <Link
                key={index}
                href={item.link || "#"}
                target={item.target || "_self"}
                className="flex items-baseline text-left gap-4 w-2/3 justify-between group hover:text-blue-light focus:text-blue-light rounded px-2 py-1"
                role="menuitem"
                onClick={(e) => {
                  onClose?.();
                  onLinkClick?.(item, title);
                }}
              >
                <span className="flex items-center gap-4">
                  {itemIconType && (
                    <Icon type={itemIconType} className="w-7 h-7" />
                  )}
                  {item.title}
                </span>
              </Link>
            )
          ) : (
            <button
              key={index}
              onClick={() => onItemClick?.(item)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onItemClick?.(item);
                }
              }}
              className="flex items-center gap-4 w-2/3 justify-start group hover:text-blue-light focus:text-blue-light rounded px-2 py-1 text-left"
              role="menuitem"
              aria-expanded={item.children ? "false" : undefined}
            >
              {item.title}
              {item.children && (
                <Icon type="chevron" className="w-5 h-5" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default NavigationSidebar;
